// Using window.axios which is pre-configured with credentials
// Declare the global axios for TypeScript
import { AxiosStatic } from 'axios';

declare global {
  interface Window {
    axios: AxiosStatic;
  }
}
import { useAuthStore } from '../stores/auth.ts';

// Token refresh configuration
const REFRESH_BUFFER_TIME = 60 * 1000; // 1 minute buffer before expiry
let refreshTimeout: ReturnType<typeof setTimeout> | null = null;
let isInitialized = false;

// Track interceptor IDs for cleanup
let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;

/**
 * Set up token refresh mechanism
 */
export const setupTokenRefresh = async () => {
  // Clear any existing timeout
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }

  // Function to get cookie by name
  const getCookie = (name: string): string | null => {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(name + '=')) {
        return cookie.substring(name.length + 1);
      }
    }
    return null;
  };

  // Check authentication status from store
  const authStore = useAuthStore();

  if (!authStore.isAuthenticated) {
    return; // Not authenticated, nothing to do
  }

  try {
    // Use the singleton validation function to prevent duplicate requests
    const response = await validateTokenSingleton();

    if (response.status === 200) {
      // Schedule regular token refresh - refresh every 10 minutes to stay well ahead of 15 min expiry
      const refreshInterval = 10 * 60 * 1000; // 10 minutes
      refreshTimeout = setTimeout(refreshToken, refreshInterval);
    }
  } catch (error) {
    // If validation fails, attempt an immediate token refresh
    refreshToken();
  }
};

/**
 * Refresh the authentication tokens
 */
// Track the last refresh time to prevent refresh loops
let lastRefreshTime = 0;
const REFRESH_COOLDOWN = 5000; // 5 seconds minimum between refresh attempts

export const refreshToken = async () => {
  // Prevent refresh loops by enforcing a cooldown period
  const now = Date.now();
  if (now - lastRefreshTime < REFRESH_COOLDOWN) {
    return false;
  }

  lastRefreshTime = now;

  try {
    const response = await window.axios.post('/api/v1/refresh_token');

    if (response.status === 200) {
      // Clear any cached validation responses since tokens have changed
      validateTokenCache = null;
      validateTokenCacheTime = 0;
      pendingValidationRequest = null;

      // Update auth store with user info if provided
      const authStore = useAuthStore();
      if (response.data?.user) {
        authStore.user = response.data.user;
      }

      // Schedule the next token refresh - use consistent 10 minute interval
      const refreshInterval = 10 * 60 * 1000; // 10 minutes
      refreshTimeout = setTimeout(refreshToken, refreshInterval);
      return true;
    }
    return false;
  } catch (error) {
    if (window.axios.isAxiosError(error) && error.response?.status === 401) {
      // Clear cache since tokens are invalid
      validateTokenCache = null;
      validateTokenCacheTime = 0;
      pendingValidationRequest = null;

      // Only logout if we're currently authenticated (avoid logout loops during initialization)
      const authStore = useAuthStore();
      if (authStore.isAuthenticated) {
        authStore.logout();
      }
    }

    return false;
  }
};

// Global caching for token validation requests
let validateTokenCache: any = null;
let validateTokenCacheTime = 0;
const CACHE_DURATION = 3000; // 3 seconds

// Keep track of in-flight validation request to deduplicate simultaneous calls
let pendingValidationRequest: Promise<any> | null = null;

/**
 * Creates a singleton validation request that all components can share
 */
export const validateTokenSingleton = async () => {
  const now = Date.now();

  // Return cached response if available and recent
  if (validateTokenCache && now - validateTokenCacheTime < CACHE_DURATION) {
    return validateTokenCache;
  }

  // If there's already a request in flight, return that promise
  if (pendingValidationRequest) {
    return pendingValidationRequest;
  }

  // Create a new request
  pendingValidationRequest = window.axios.get('/api/v1/validate_token', {
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    },
    // This is the critical part - it ensures cookies (including HttpOnly cookies) are sent with the request
    withCredentials: true
  })
    .then(response => {
      validateTokenCache = response;
      validateTokenCacheTime = Date.now();
      return response;
    })
    .catch(error => {
      throw error;
    })
    .finally(() => {
      // Clear the pending request reference after completion
      setTimeout(() => {
        pendingValidationRequest = null;
      }, 100);
    });

  return pendingValidationRequest;
}

// Export a function to initialize token refresh when the app starts
export const initializeTokenRefresh = () => {
  // Prevent duplicate initialization
  if (isInitialized) {
    return;
  }

  isInitialized = true;

  // Add axios request interceptor for caching validation requests
  // Store the interceptor ID so we can remove it later if needed
  requestInterceptorId = window.axios.interceptors.request.use(
    async (config) => {
      // Only apply caching to validation endpoint GET requests
      if (config.url === '/api/v1/validate_token' && config.method === 'get') {
        const now = Date.now();

        // If we have a recent cached response, cancel this request and use cache
        if (validateTokenCache && now - validateTokenCacheTime < CACHE_DURATION) {
          // This creates a canceled request that will be handled in the response interceptor
          const source = window.axios.CancelToken.source();
          config.cancelToken = source.token;
          source.cancel('Using cached validation response');
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Add axios response interceptor for caching validation responses and handling 401 errors
  // Store the interceptor ID so we can remove it later if needed
  responseInterceptorId = window.axios.interceptors.response.use(
    (response) => {
      // Cache successful validation responses
      if (response.config.url === '/api/v1/validate_token' && response.config.method === 'get') {
        validateTokenCache = response;
        validateTokenCacheTime = Date.now();
      }
      return response;
    },
    async (error) => {
      // If request was canceled due to cache, return the cached response
      if (window.axios.isCancel(error) && error.message === 'Using cached validation response') {
        return validateTokenCache;
      }

      const originalRequest = error.config;

      // If the error is unauthorized and we haven't tried refreshing yet
      if (error.response && error.response.status === 401) {
        // Don't retry for validate_token endpoint to avoid loops
        if (error.config.url !== '/api/v1/validate_token') {
          // Try to refresh the token
          const refreshSuccessful = await refreshToken();
          if (refreshSuccessful) {
            // Retry the original request
            return window.axios(originalRequest);
          }
        }
      }

      // If refresh didn't work or this isn't a 401 error, pass it through
      return Promise.reject(error);
    }
  );

  // Set up initial token refresh scheduling
  setupTokenRefresh();
};

/**
 * Clean up token refresh service
 * Removes all timeouts, interceptors, and cached data
 * Call this when the app unmounts or during logout
 */
export const cleanupTokenRefresh = () => {
  // Clear any pending timeout
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }

  // Eject axios interceptors if they exist
  if (requestInterceptorId !== null) {
    window.axios.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }

  if (responseInterceptorId !== null) {
    window.axios.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }

  // Clear all cached data
  validateTokenCache = null;
  validateTokenCacheTime = 0;
  pendingValidationRequest = null;

  // Reset initialization flag so service can be re-initialized if needed
  isInitialized = false;
};
